/**
 * The test hooks: with `?debug` in the address, `window.__developerApp` exposes the services
 * to the browser checks (`ui/e2e/run.mjs`), including a switch that makes a window crash on
 * purpose (to check that crashes stay inside the window).
 */
import { unwrap } from "solid-js/store";
import { startOrder } from "../shared/subsystems";
import { hitAt, pinPoint, plusPoint, selectionFocus } from "../state/diagram";
import type { App } from "./context";
import { SUBSYSTEMS } from "./subsystems";

/** Installs the hooks when the page was opened with `?debug`. */
export function installDebugHooks(app: App): void {
  if (!new URLSearchParams(location.search).has("debug")) return;
  const { scene } = app;
  Object.assign(window, {
    __developerApp: {
      services: app,
      /** The subsystems in start order: id, what it does, what it needs. */
      subsystems: () => startOrder(SUBSYSTEMS, ["platform", "windows"]).map((s) => ({ id: s.id, summary: s.summary, needs: [...s.needs] })),
      theme: app.theme,
      graph: () => unwrap(app.graph.state.graph),
      /** The whole graph, every environment's part (`graph()` is the current environment's). */
      fullGraph: () => unwrap(app.graph.full()),
      scene,
      /** Screen position (relative to the canvas) of a node, for clicking it in tests. */
      nodeOnScreen(id: string) {
        const n = scene.byId.get(id);
        return n ? { x: scene.sx(n), y: scene.sy(n), visible: n.visible } : null;
      },
      /** A box of the class diagram on screen (relative to its canvas): corner, size and centre. */
      boxOnScreen(id: string) {
        const d = app.diagram;
        const b = d.layout().byId.get(id);
        if (!b) return null;
        const { x, y, k } = d.view;
        return { x: b.x * k + x, y: b.y * k + y, w: b.w * k, h: b.h * k, cx: (b.x + b.w / 2) * k + x, cy: (b.y + b.h / 2) * k + y };
      },
      /** Where pin `pin` of a blueprint box is on its canvas (`null`: no such box or pin). */
      pinOnScreen(id: string, pin: string) {
        const d = app.diagram;
        const b = d.layout().byId.get(id);
        const index = b ? b.pins.findIndex((p) => p.id === pin) : -1;
        if (!b || index < 0) return null;
        const at = pinPoint(b, index);
        return { x: at.x * d.view.k + d.view.x, y: at.y * d.view.k + d.view.y };
      },
      /** Where the "+" adding a pin to a blueprint box is on its canvas. */
      plusOnScreen(id: string) {
        const d = app.diagram;
        const b = d.layout().byId.get(id);
        if (!b) return null;
        const at = plusPoint(b);
        return { x: at.x * d.view.k + d.view.x, y: at.y * d.view.k + d.view.y };
      },
      /** Where a closed bundle's count of the class diagram is on its canvas (`null`: no such bundle, or open). */
      diagramBundleOnScreen(key: string) {
        const d = app.diagram;
        const b = d.layout().bundleByKey.get(key);
        if (!b) return null;
        const { x, y, k } = d.view;
        return { x: ((b.x1 + b.x2) / 2) * k + x, y: ((b.y1 + b.y2) / 2) * k + y, links: b.links.length };
      },
      /** What the class diagram has at a point of its canvas, with the current selection: `box`, `edge`, `bundle`, `package` or `background`. */
      diagramAt(x: number, y: number) {
        const d = app.diagram;
        const w = d.toWorld(x, y);
        return hitAt(d.layout(), w.x, w.y, 6 / d.view.k, selectionFocus(app.view.state.selection)).type;
      },
      /** A package of the class diagram on screen (relative to its canvas): its tab's middle, its body's middle, and how many boxes it hides when folded. */
      packageOnScreen(id: string) {
        const d = app.diagram;
        const p = d.layout().packageById.get(id);
        if (!p) return null;
        const { x, y, k } = d.view;
        return { tabX: (p.x + 30) * k + x, tabY: (p.y + 11) * k + y, cx: (p.x + p.w / 2) * k + x, cy: (p.y + p.h - 12) * k + y, folded: p.folded ?? null };
      },
      /** A group's circle on screen (relative to the canvas) and the centre of its name, for dragging it in tests. */
      groupOnScreen(id: string) {
        const g = scene.groupById.get(id);
        if (!g?.visible) return null;
        scene.layoutGroups();
        const label = scene.groupLabel(g);
        const c = scene.groupCircle(g);
        return { ...c, labelX: label.x + label.w / 2, labelY: label.y + label.h / 2, depth: g.depth };
      },
      /** The closed bundles on screen (nothing selected): key, size, and the middle of their line (relative to the canvas). */
      bundlesOnScreen() {
        const none = { node: null, group: null, link: null, bundle: null };
        return scene.bundles.flatMap((b) => {
          const seg = scene.bundleSegment(b);
          return seg && !scene.isOpen(b, none) ? [{ key: b.key, count: b.links.length, x: (seg.x1 + seg.x2) / 2, y: (seg.y1 + seg.y2) / 2 }] : [];
        });
      },
      /** What a click on the nodal view's canvas at a point would pick first, nothing selected: a group's `name`, a bundle's `count`, or `other`. */
      nodalAt(x: number, y: number) {
        const none = { node: null, group: null, link: null, bundle: null };
        return scene.hitGroupLabel(x, y) ? "name" : scene.hitBundleCount(x, y, none) ? "count" : "other";
      },
      crash: (window: string) => app.wm.setCrashed(window, true),
    },
  });
}

/** Sends GUI errors that escape every window to the core's log, so they are not lost. */
export function reportUncaughtErrors(app: App): void {
  addEventListener("error", (e) => app.actions.log("app", "error", `uncaught: ${e.message}`));
  addEventListener("unhandledrejection", (e) => app.actions.log("app", "error", `unhandled promise rejection: ${String(e.reason)}`));
}
